import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { useIsCraneHire, useIsConcretePumping } from '../../hooks/useCurrentIndustry';
import { formatTime as formatTimeUtil, formatTimeFromDate } from '../../utils/dateFormatting';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { WheelTimePicker } from '../../components/mobile/WheelTimePicker';
import { toast } from 'sonner';
import { 
  ChevronLeft, 
  Loader2,
  FileText,
  Truck,
  Wrench,
  Check,
  PenTool,
  Trash2,
  X,
  User,
  Home,
  Package,
  Plus,
  Pencil,
  Mountain,
  Camera,
  Copy,
  Receipt,
  Minus,
  MapPin,
  Layers,
  CheckCircle2,
  Calendar,
  Clock
} from 'lucide-react';
import { deriveDepotTravel } from '../../lib/docketTimes';

const MobileCreateDocket = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const jobDate = searchParams.get('date');
  const { api, operator, tenantSettings, isFlagEnabled, refreshTenantSettings, refreshPlatformFlags } = useMobileAuth();
  const isCraneHire = useIsCraneHire();
  const isConcretePumping = useIsConcretePumping();
  
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  
  // Docket items
  const [items, setItems] = useState([]);
  const [workCompleted, setWorkCompleted] = useState(''); // Single work completed field
  const [notes, setNotes] = useState('');
  
  // Customer sign-off
  const [customerName, setCustomerName] = useState('');
  const [customerConfirmed, setCustomerConfirmed] = useState(false);
  const [signature, setSignature] = useState(null);
  const [signatureSkipped, setSignatureSkipped] = useState(false);
  const [skipReason, setSkipReason] = useState('');
  const [skipNotes, setSkipNotes] = useState('');
  
  // Driver sign-off
  const [driverName, setDriverName] = useState('');
  const [driverConfirmed, setDriverConfirmed] = useState(false);
  const [hasDriverSignature, setHasDriverSignature] = useState(false);
  const [isDriverDrawing, setIsDriverDrawing] = useState(false);
  const driverCanvasRef = useRef(null);
  
  // Signature canvas
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  
  // Material loads
  const [materialLoads, setMaterialLoads] = useState([]);
  // Tolls added to this docket: [{ toll_id, name, price, quantity }]
  const [availableTolls, setAvailableTolls] = useState([]);
  const [docketTolls, setDocketTolls] = useState([]);
  const [tollDialogOpen, setTollDialogOpen] = useState(false);
  // Concrete pumping: individual concrete load entries
  const [concreteLoads, setConcreteLoads] = useState([]); // [{ material_type_id, material_name, quantity }]
  const [concreteDialogOpen, setConcreteDialogOpen] = useState(false);
  const [concreteForm, setConcreteForm] = useState({ material_type_id: '', quantity: '' });
  const [yards, setYards] = useState([]);
  const [materialTypes, setMaterialTypes] = useState([]);
  const [availableAttachmentTypes, setAvailableAttachmentTypes] = useState([]);
  const [jobAttachmentTypeIds, setJobAttachmentTypeIds] = useState([]);
  const [attachmentItems, setAttachmentItems] = useState([]);
  const [attachmentDialogOpen, setAttachmentDialogOpen] = useState(false);
  const [attachmentForm, setAttachmentForm] = useState({ attachment_type_id: '', qty: '' });
  const [loadDialogOpen, setLoadDialogOpen] = useState(false);
  const [editingLoadIndex, setEditingLoadIndex] = useState(null);
  const [loadForm, setLoadForm] = useState({
    yard_id: '',
    material_type_id: '',
    load_type: 'pickup',
    quantity: '',
    unit: 'tonne',
    tip_docket_number: '',
    tip_docket_photo: null,
    notes: '',
    on_customer_account: false,
  });
  const photoInputRef = useRef(null);
  
  // Docket photos
  const [docketPhotos, setDocketPhotos] = useState([]);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const cameraInputRef = useRef(null);
  const libraryInputRef = useRef(null);
  const MAX_PHOTOS = 10;

  // For haulage / multi-stop end-of-day dockets the driver signs at the depot
  const isDepotJob = job?.job_type === 'haulage' || job?.requires_multi_stops;
  const depotMode = !isDepotJob && !!(isFlagEnabled('docket_depot_times') && tenantSettings?.docket_depot_times_enabled);
  useEffect(() => {
    if (operator?.name && !driverName) {
      setDriverName(operator.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job, operator]);

  useEffect(() => {
    fetchJobDetail();
    fetchMaterialYards();
    fetchTolls();
    // Refresh tenant settings + flags so depot mode is current
    refreshTenantSettings?.();
    refreshPlatformFlags?.();
  }, [jobId]);

  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  }, [job]);

  useEffect(() => {
    if (driverCanvasRef.current) {
      const canvas = driverCanvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
    }
  }, [job]);

  const fetchMaterialYards = async () => {
    try {
      const response = await api.get('/api/mobile/material-yards');
      setYards(response.data.yards || []);
      setMaterialTypes(response.data.material_types || []);
    } catch (error) {
      console.error('Failed to fetch material yards', error);
    }
  };

  const fetchTolls = async () => {
    try {
      const response = await api.get('/api/mobile/tolls');
      setAvailableTolls(response.data || []);
    } catch (error) {
      console.error('Failed to fetch tolls', error);
    }
  };

  const handleAddToll = (toll) => {
    setDocketTolls(prev => {
      const existing = prev.find(t => t.toll_id === toll.id);
      if (existing) {
        return prev.map(t => t.toll_id === toll.id ? { ...t, quantity: t.quantity + 1 } : t);
      }
      return [...prev, { toll_id: toll.id, name: toll.name, price: toll.price || 0, quantity: 1 }];
    });
    setTollDialogOpen(false);
  };

  const updateTollQty = (tollId, delta) => {
    setDocketTolls(prev => prev
      .map(t => t.toll_id === tollId ? { ...t, quantity: Math.max(0, t.quantity + delta) } : t)
      .filter(t => t.quantity > 0));
  };

  const fetchJobDetail = async () => {
    try {
      const params = jobDate ? { viewing_date: jobDate } : {};
      const response = await api.get(`/api/mobile/jobs/${jobId}`, { params });
      setJob(response.data);
      
      // Initialize items from job
      const initialItems = [];
      
      // Tower crane jobs: only add labour items, no equipment line
      const isTowerCrane = response.data.is_tower_crane;
      
      if (!isTowerCrane) {
        // Get equipment info from allocation
        const equipment = response.data.equipment;
        const equipmentRego = equipment?.registration || equipment?.name || 'N/A';
        
        // Add equipment type item with default times (8am-5pm = 9 hours)
        initialItems.push({
          id: 'equipment-' + Date.now(),
          item_type: 'equipment',
          type_name: response.data.equipment_type,
          asset_id: equipmentRego, // Equipment registration/ID
          qty: 9,
          uom: 'HOUR',
          start_time: '08:00',
          finish_time: '17:00',
          lunch_minutes: 0,
          travel_minutes: 0,
          depart_depot_time: null,
          arrive_depot_time: null,
          travel_out_minutes: null,
          travel_return_minutes: null,
          // SMU defaults — pre-fill start_smu from the asset's current meter, and
          // pull custom labels (e.g. "Hours" / "KM") for display.
          start_smu: equipment?.hours_kms ?? '',
          finish_smu: '',
          start_smu_2: equipment?.hours_kms_2 ?? '',
          finish_smu_2: '',
          smu_unit: equipment?.smu_primary_label || 'Hours',
          smu_unit_2: equipment?.smu_secondary_label || 'KM',
        });

        // Separate Primary Operator Line — when enabled, add a worker line for the
        // primary operator so their hours can differ from the equipment hours.
        if (tenantSettings?.separate_operator_line) {
          const allocation = response.data.allocation;
          const operatorId = allocation?.operator_id;
          const workerAllocations = response.data.worker_allocations || [];
          const workerTypes = response.data.worker_types || [];

          // The operator's worker type can come from multiple places:
          // 1. Their worker_allocation (if the operator was also allocated as a worker)
          // 2. The operator's own worker record (worker_type_ids array)
          // 3. The allocation's worker_type_id (rarely set)
          const operatorWa = workerAllocations.find(
            wa => wa.operator_id === operatorId
          );
          // The mobile job endpoint returns 'operator_info' with the full worker record
          const operatorInfo = response.data.operator_info;
          const operatorWorkerTypeId = operatorWa?.worker_type_id
            || (operatorInfo?.worker_type_ids && operatorInfo.worker_type_ids[0])
            || operatorInfo?.worker_type_id
            || allocation?.worker_type_id;
          const operatorWorkerName = operatorWa?.worker_name || operatorInfo?.name || operator?.name;

          if (operatorWorkerTypeId) {
            const workerType = workerTypes.find(wt => wt.id === operatorWorkerTypeId);
            initialItems.push({
              id: 'primary-operator-' + Date.now(),
              item_type: 'worker',
              type_name: workerType?.name || 'Operator',
              display_name: operatorWorkerName ? `${workerType?.name || 'Operator'} - ${operatorWorkerName}` : (workerType?.name || 'Operator'),
              worker_name: operatorWorkerName || 'Primary Operator',
              worker_type_id: operatorWorkerTypeId,
              is_primary_operator: true,
              qty: 9,
              uom: 'HOUR',
              start_time: '08:00',
              finish_time: '17:00',
              lunch_minutes: 0,
              travel_minutes: 0,
              depart_depot_time: null,
              arrive_depot_time: null,
              travel_out_minutes: null,
              travel_return_minutes: null,
            });
          }
        }
      }
      
      // Store all compatible attachment types for the Attachments section
      const allAttTypes = response.data.attachment_types || [];
      setAvailableAttachmentTypes(allAttTypes);
      setJobAttachmentTypeIds(response.data.attachment_type_ids || []);
      
      // Add worker type items from worker_allocations (actual workers assigned)
      // or fall back to worker_requirements if no allocations
      const workerAllocations = response.data.worker_allocations || [];
      const workerTypes = response.data.worker_types || [];
      
      if (workerAllocations.length > 0) {
        // When separate_operator_line is on, the primary operator was already added
        // above — skip their worker allocation to avoid a duplicate line.
        const primaryOperatorId = tenantSettings?.separate_operator_line
          ? response.data.allocation?.operator_id
          : null;
        let primaryOperatorSkipped = false;

        // Use actual worker allocations - includes worker names
        workerAllocations.forEach((wa, idx) => {
          // Skip the first matching worker allocation for the primary operator
          if (primaryOperatorId && wa.operator_id === primaryOperatorId && !primaryOperatorSkipped) {
            primaryOperatorSkipped = true;
            return;
          }
          const workerType = workerTypes.find(wt => wt.id === wa.worker_type_id);
          initialItems.push({
            id: 'worker-' + wa.id + '-' + Date.now(),
            item_type: 'worker',
            type_name: workerType?.name || 'Worker',
            worker_name: wa.worker_name || 'Assigned Worker', // Actual worker name
            worker_type_id: wa.worker_type_id,
            qty: 9,
            uom: 'HOUR',
            start_time: '08:00',
            finish_time: '17:00',
            lunch_minutes: 0,
            travel_minutes: 0,
            depart_depot_time: null,
            arrive_depot_time: null,
            travel_out_minutes: null,
            travel_return_minutes: null,
          });
        });
      } else if (response.data.worker_requirements) {
        // Fall back to requirements if no allocations
        response.data.worker_requirements.forEach((wr, idx) => {
          const workerType = workerTypes.find(wt => wt.id === wr.worker_type_id);
          if (workerType) {
            for (let i = 0; i < wr.quantity; i++) {
              initialItems.push({
                id: 'worker-' + wr.worker_type_id + '-' + i + '-' + Date.now(),
                item_type: 'worker',
                type_name: workerType.name,
                worker_name: 'Not Assigned',
                worker_type_id: wr.worker_type_id,
                qty: 9,
                uom: 'HOUR',
                start_time: '08:00',
                finish_time: '17:00',
                lunch_minutes: 0,
                travel_minutes: 0,
                depart_depot_time: null,
                arrive_depot_time: null,
                travel_out_minutes: null,
                travel_return_minutes: null,
              });
            }
          }
        });
      }
      
      setItems(initialItems);
    } catch (error) {
      toast.error('Failed to fetch job details');
    } finally {
      setLoading(false);
    }
  };

  const handleItemChange = (itemId, field, value) => {
    setItems(prev => prev.map(item => {
      if (item.id === itemId) {
        const updatedItem = { ...item, [field]: value };
        
        // In depot mode, derive travel from depot times
        if (depotMode && (field === 'depart_depot_time' || field === 'arrive_depot_time' || field === 'start_time' || field === 'finish_time') && updatedItem.item_type !== 'attachment') {
          const derived = deriveDepotTravel(updatedItem);
          if (derived) {
            updatedItem.travel_out_minutes = derived.travel_out_minutes;
            updatedItem.travel_return_minutes = derived.travel_return_minutes;
            updatedItem.travel_minutes = derived.travel_minutes;
          } else {
            updatedItem.travel_out_minutes = null;
            updatedItem.travel_return_minutes = null;
            // Don't clear travel_minutes when derivation fails — leave for manual or 0
          }
        }

        // Calculate hours if time fields are set and uom is HOUR
        // Formula: (Finish - Start) - Lunch + Travel
        if ((field === 'start_time' || field === 'finish_time' || field === 'lunch_minutes' || field === 'travel_minutes' || field === 'depart_depot_time' || field === 'arrive_depot_time') && updatedItem.uom === 'HOUR') {
          if (updatedItem.start_time && updatedItem.finish_time) {
            const hours = calculateHours(
              updatedItem.start_time, 
              updatedItem.finish_time,
              updatedItem.lunch_minutes || 0,
              updatedItem.travel_minutes || 0
            );
            updatedItem.qty = hours;
          }
        }
        
        return updatedItem;
      }
      return item;
    }));
  };

  const calculateHours = (startTime, finishTime, lunchMinutes = 0, travelMinutes = 0) => {
    try {
      const [startHour, startMin] = startTime.split(':').map(Number);
      const [finishHour, finishMin] = finishTime.split(':').map(Number);
      
      let startMinutes = startHour * 60 + startMin;
      let finishMinutes = finishHour * 60 + finishMin;
      
      // Handle overnight shifts
      if (finishMinutes < startMinutes) {
        finishMinutes += 24 * 60;
      }
      
      // Calculate: (Finish - Start) - Lunch + Travel
      const totalMinutes = finishMinutes - startMinutes - lunchMinutes + travelMinutes;
      return Math.round((totalMinutes / 60) * 100) / 100; // Round to 2 decimals
    } catch (error) {
      return 0;
    }
  };

  // Generate hours (00-23) and minutes (00, 05, 10, ..., 55) for dropdowns
  const hours = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, '0'));
  const minutes = Array.from({ length: 12 }, (_, i) => String(i * 5).padStart(2, '0'));
  
  // Generate lunch options in 15-minute increments (0, 15, 30, ..., 180)
  const lunchTravelOptions = Array.from({ length: 13 }, (_, i) => i * 15);
  // Travel time allows a longer range — up to 240 mins (EW2-302)
  const travelOptions = Array.from({ length: 17 }, (_, i) => i * 15);

  const hasWorkerItems = items.some(i => i.item_type === 'worker');

  const handleCopyTimesToWorkers = () => {
    const equipmentItem = items.find(i => i.item_type === 'equipment');
    if (!equipmentItem) return;
    setItems(prev => prev.map(item => {
      if (item.item_type !== 'worker') return item;
      const updated = {
        ...item,
        start_time: equipmentItem.start_time,
        finish_time: equipmentItem.finish_time,
        lunch_minutes: equipmentItem.lunch_minutes,
        travel_minutes: equipmentItem.travel_minutes,
        depart_depot_time: equipmentItem.depart_depot_time,
        arrive_depot_time: equipmentItem.arrive_depot_time,
        travel_out_minutes: equipmentItem.travel_out_minutes,
        travel_return_minutes: equipmentItem.travel_return_minutes,
      };
      if (updated.start_time && updated.finish_time && updated.uom === 'HOUR') {
        updated.qty = calculateHours(updated.start_time, updated.finish_time, updated.lunch_minutes || 0, updated.travel_minutes || 0);
      }
      return updated;
    }));
    toast.success('Times copied to all workers');
  };

  const handleTimeChange = (itemId, timeType, timePart, value) => {
    const item = items.find(i => i.id === itemId);
    if (!item) return;

    const currentTime = item[timeType] || '00:00';
    const [currentHour, currentMin] = currentTime.split(':');
    
    const newTime = timePart === 'hour' 
      ? `${value}:${currentMin}`
      : `${currentHour}:${value}`;
    
    handleItemChange(itemId, timeType, newTime);
  };

  // Signature drawing handlers
  const getCoords = (e, cvs) => {
    const canvas = cvs || canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top) * scaleY
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    };
  };

  const startDrawing = (e) => {
    e.preventDefault();
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoords(e);
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoords(e);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasSignature(false);
  };

  // Driver signature canvas helpers
  const startDriverDrawing = (e) => {
    e.preventDefault();
    setIsDriverDrawing(true);
    const canvas = driverCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoords(e, canvas);
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };
  const drawDriver = (e) => {
    if (!isDriverDrawing) return;
    e.preventDefault();
    const canvas = driverCanvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoords(e, canvas);
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    setHasDriverSignature(true);
  };
  const stopDriverDrawing = () => setIsDriverDrawing(false);
  const clearDriverSignature = () => {
    const canvas = driverCanvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDriverSignature(false);
  };

  // Material Load Functions
  const resetLoadForm = () => {
    setLoadForm({
      yard_id: '',
      material_type_id: '',
      load_type: 'pickup',
      quantity: '',
      unit: 'tonne',
      tip_docket_number: '',
      tip_docket_photo: null,
      notes: '',
      on_customer_account: false,
    });
    setEditingLoadIndex(null);
  };

  const openLoadDialog = (index = null) => {
    if (index !== null) {
      const load = materialLoads[index];
      setLoadForm({
        yard_id: load.yard_id,
        material_type_id: load.material_type_id,
        load_type: load.load_type,
        quantity: load.quantity.toString(),
        unit: load.unit,
        tip_docket_number: load.tip_docket_number || '',
        tip_docket_photo: load.tip_docket_photo || null,
        notes: load.notes || '',
        on_customer_account: !!load.on_customer_account,
      });
      setEditingLoadIndex(index);
    } else {
      resetLoadForm();
    }
    setLoadDialogOpen(true);
  };

  const handlePhotoCapture = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setLoadForm(prev => ({ ...prev, tip_docket_photo: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveLoad = () => {
    // Validation
    if (!loadForm.yard_id) {
      toast.error('Please select a location');
      return;
    }
    if (!loadForm.material_type_id) {
      toast.error('Please select a material type');
      return;
    }
    if (!loadForm.quantity || parseFloat(loadForm.quantity) <= 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    const selectedYard = yards.find(y => y.id === loadForm.yard_id);
    const selectedMaterial = materialTypes.find(m => m.id === loadForm.material_type_id);

    const newLoad = {
      yard_id: loadForm.yard_id,
      yard_name: selectedYard?.name || 'Unknown',
      material_type_id: loadForm.material_type_id,
      material_name: selectedMaterial?.name || 'Unknown',
      load_type: loadForm.load_type,
      quantity: parseFloat(loadForm.quantity),
      unit: loadForm.unit,
      tip_docket_number: loadForm.tip_docket_number || null,
      tip_docket_photo: loadForm.tip_docket_photo || null,
      notes: loadForm.notes || null,
      on_customer_account: !!loadForm.on_customer_account,
    };

    if (editingLoadIndex !== null) {
      const updated = [...materialLoads];
      updated[editingLoadIndex] = newLoad;
      setMaterialLoads(updated);
      toast.success('Load updated');
    } else {
      setMaterialLoads(prev => [...prev, newLoad]);
      toast.success('Load added');
    }

    setLoadDialogOpen(false);
    resetLoadForm();
  };

  const handleDeleteLoad = (index) => {
    setMaterialLoads(prev => prev.filter((_, i) => i !== index));
    toast.success('Load removed');
  };

  const getAvailableMaterials = () => {
    // If a yard is selected, filter materials to those available at that yard
    if (loadForm.yard_id) {
      const yard = yards.find(y => y.id === loadForm.yard_id);
      if (yard?.materials) {
        const yardMaterialIds = yard.materials.map(m => m.material_type_id);
        return materialTypes.filter(mt => yardMaterialIds.includes(mt.id));
      }
    }
    return materialTypes;
  };

  const getAvailableUnits = () => {
    // If yard and material are selected, get available units for that combo
    if (loadForm.yard_id && loadForm.material_type_id) {
      const yard = yards.find(y => y.id === loadForm.yard_id);
      if (yard?.materials) {
        const matchingMaterials = yard.materials.filter(m => m.material_type_id === loadForm.material_type_id);
        return matchingMaterials.map(m => m.unit);
      }
    }
    return ['tonne', 'm3', 'load'];
  };

  const getLoadTypeLabel = (type) => {
    const labels = { pickup: 'Pickup', disposal: 'Disposal', buyback: 'Buy Back' };
    return labels[type] || type;
  };

  const getLoadTypeColor = (type) => {
    const colors = {
      pickup: 'bg-blue-500/20 text-blue-400',
      disposal: 'bg-orange-500/20 text-orange-400',
      buyback: 'bg-green-500/20 text-green-400'
    };
    return colors[type] || 'bg-gray-500/20 text-[var(--m-text-secondary)]';
  };

  // ── Docket Photo Handlers ──────────────────────────────────────
  const handleDocketPhotoCapture = async (e, source) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (docketPhotos.length >= MAX_PHOTOS) {
      toast.error(`Maximum ${MAX_PHOTOS} photos allowed`);
      return;
    }
    if (file.size > 15 * 1024 * 1024) {
      toast.error('Photo must be under 15MB');
      return;
    }

    setUploadingPhoto(true);
    try {
      // Read file as base64
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      const capturedAt = new Date().toISOString();

      const res = await api.post('/api/mobile/docket-photos/upload', {
        photo: base64,
        captured_at: capturedAt,
        source
      });

      setDocketPhotos(prev => [...prev, res.data.photo]);
      toast.success('Photo added');
    } catch (err) {
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      // Reset input so same file can be re-selected
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      if (libraryInputRef.current) libraryInputRef.current.value = '';
    }
  };

  const removePhoto = (photoId) => {
    setDocketPhotos(prev => prev.filter(p => p.id !== photoId));
  };

  const handleSubmit = async () => {
    setSubmitAttempted(true);
    // Validation
    const filledItems = items.filter(item => item.qty > 0);
    // Concrete pumping jobs can submit with only concrete loads and no worker/equipment hours
    if (filledItems.length === 0 && !(isConcretePumping && concreteLoads.length > 0)) {
      toast.error('Please enter hours for at least one item');
      return;
    }

    // SMU readings required when tenant flag is on
    const smuRules = tenantSettings?.field_rules?.smu;
    if (smuRules?.enabled && smuRules?.required_to_complete_job) {
      const missing = filledItems.find(
        (i) => i.item_type === 'equipment' && (i.start_smu == null || i.start_smu === '' || i.finish_smu == null || i.finish_smu === '')
      );
      if (missing) {
        toast.error(`Meter readings required for ${missing.type_name}`);
        return;
      }
    }
    
    // End-of-day signature is only required when the job's signature timing is
    // 'end' or 'both'. When the customer signs at the START of the day, the
    // end-of-day signature is optional.
    const sigTiming = job?.signature_config?.timing || 'end';
    const endSignatureRequired = sigTiming === 'end' || sigTiming === 'both';
    if (endSignatureRequired) {
      if (!signatureSkipped) {
        if (!customerName.trim()) {
          toast.error('Please enter customer name for sign-off');
          return;
        }
        if (!customerConfirmed) {
          toast.error('Customer must confirm the docket');
          return;
        }
        if (!hasSignature) {
          toast.error('Customer signature is required. Tick "Unable to capture signature" if the customer cannot sign.');
          return;
        }
      } else {
        if (!skipReason) {
          toast.error('Please select a reason for skipping signature');
          return;
        }
      }
    }

    // Concrete pumping: at least one concrete load is required
    if (isConcretePumping) {
      if (concreteLoads.length === 0) {
        toast.error('Please add at least one concrete load entry');
        return;
      }
    }

    // Driver sign-off is always required
    if (!driverName.trim()) {
      toast.error('Please enter driver name for sign-off');
      return;
    }
    if (!driverConfirmed) {
      toast.error('Driver must confirm the docket');
      return;
    }

    // Depot time validation (when depot mode is on)
    if (depotMode) {
      const depotMissing = filledItems.find(
        (i) => i.item_type !== 'attachment' && i.uom === 'HOUR' && (!i.depart_depot_time || !i.arrive_depot_time)
      );
      if (depotMissing) {
        toast.error('Enter Depart Depot and Arrive Depot times');
        return;
      }
      for (const i of filledItems) {
        if (i.item_type === 'attachment') continue;
        if ((i.travel_out_minutes || 0) > 720) {
          toast.error(`${i.type_name}: Depart Depot must be before Arrive Site`);
          return;
        }
        if ((i.travel_return_minutes || 0) > 720) {
          toast.error(`${i.type_name}: Arrive Depot must be after Depart Site`);
          return;
        }
      }
    }

    setSaving(true);
    
    try {
      // Get customer signature as base64 if drawn
      let signatureData = null;
      if (hasSignature && canvasRef.current) {
        signatureData = canvasRef.current.toDataURL('image/png');
      }
      
      // Get driver signature as base64 if drawn
      let driverSignatureData = null;
      if (hasDriverSignature && driverCanvasRef.current) {
        driverSignatureData = driverCanvasRef.current.toDataURL('image/png');
      }
      
      // Get user's LOCAL date (not UTC) for timezone handling
      const now = new Date();
      const userToday = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      
      const docketData = {
        job_id: jobId,
        job_date: jobDate,
        user_today: userToday, // Pass user's local "today" for timezone handling
        items: [
          ...filledItems.map(item => ({
            item_type: item.item_type,
            type_name: item.type_name,
            qty: parseFloat(item.qty) || 0,
            uom: item.uom,
            description: '',
            start_time: item.start_time || null,
            finish_time: item.finish_time || null,
            worker_type_id: item.worker_type_id || null,
            is_primary_operator: item.is_primary_operator || false,
            attachment_type_id: item.attachment_type_id || null,
            lunch_minutes: item.lunch_minutes || 0,
            travel_minutes: item.travel_minutes || 0,
            // SMU readings (only present for equipment items when tenant flag is on)
            start_smu: item.start_smu !== '' && item.start_smu != null ? parseFloat(item.start_smu) : null,
            finish_smu: item.finish_smu !== '' && item.finish_smu != null ? parseFloat(item.finish_smu) : null,
            start_smu_2: item.start_smu_2 !== '' && item.start_smu_2 != null ? parseFloat(item.start_smu_2) : null,
            finish_smu_2: item.finish_smu_2 !== '' && item.finish_smu_2 != null ? parseFloat(item.finish_smu_2) : null,
            smu_unit: item.smu_unit || null,
            smu_unit_2: item.smu_unit_2 || null,
            // Depot times (only sent when depotMode is on and not an attachment)
            ...(depotMode && item.item_type !== 'attachment' ? {
              depart_depot_time: item.depart_depot_time || null,
              arrive_depot_time: item.arrive_depot_time || null,
            } : {})
          })),
          ...attachmentItems.map(att => ({
            item_type: 'attachment',
            type_name: att.type_name,
            qty: parseFloat(att.qty) || 0,
            uom: 'HOUR',
            description: '',
            attachment_type_id: att.attachment_type_id,
            start_time: null,
            finish_time: null,
            lunch_minutes: 0,
            travel_minutes: 0
          }))
        ],
        material_loads: materialLoads.map(load => ({
          yard_id: load.yard_id,
          material_type_id: load.material_type_id,
          load_type: load.load_type,
          quantity: load.quantity,
          unit: load.unit,
          tip_docket_number: load.tip_docket_number,
          tip_docket_photo: load.tip_docket_photo,
          notes: load.notes
        })),
        concrete_pumped_m3: isConcretePumping ? concreteLoads.reduce((s, cl) => s + cl.quantity, 0) : null,
        concrete_loads: isConcretePumping ? concreteLoads.map(cl => ({
          material_type_id: cl.material_type_id,
          material_name: cl.material_name,
          quantity: cl.quantity,
        })) : [],
        notes: notes,
        work_completed: workCompleted, // Single work completed field
        tolls: docketTolls.map(t => ({ toll_id: t.toll_id, quantity: t.quantity })),
        customer_name: signatureSkipped ? null : customerName,
        customer_signature: signatureSkipped ? null : signatureData,
        customer_confirmed: signatureSkipped ? false : customerConfirmed,
        signature_skipped: signatureSkipped,
        signature_skip_reason: signatureSkipped ? skipReason : null,
        signature_skip_notes: signatureSkipped && skipNotes.trim() ? skipNotes.trim() : null,
        photos: docketPhotos.map(p => p.url),
        driver_name: driverName,
        driver_signature: driverSignatureData,
        driver_confirmed: driverConfirmed,
      };
      
      await api.post('/api/mobile/dockets', docketData);
      toast.success('Docket submitted successfully!');
      navigate('/mobile/jobs');
    } catch (error) {
      const detail = error.response?.data?.detail || '';
      if (error.response?.status === 422 && detail.includes('Depart Depot')) {
        // Server requires depot times but the UI didn't show them — refresh settings
        await Promise.all([refreshTenantSettings?.(), refreshPlatformFlags?.()]);
        toast.error('Depot times are now required — enter Depart Depot and Arrive Depot, then submit again.');
      } else {
        toast.error(detail || 'Failed to submit docket');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--m-bg)] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[var(--m-bg)] pb-6">
      {/* Header */}
      <div className="bg-[var(--m-bg-card)] px-4 py-4 sticky top-0 z-10 safe-top">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-[var(--m-text-secondary)] p-2">
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-[var(--m-text)] font-bold flex items-center gap-2">
                <FileText className="h-5 w-5 text-[#ffd108]" />
                Create Docket
              </h1>
              <p className="text-[var(--m-text-secondary)] text-xs">{job?.customer?.company_name}</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={() => navigate('/mobile/dashboard')}
            className="text-[var(--m-text-secondary)] p-2"
            data-testid="home-btn"
          >
            <Home className="h-5 w-5" />
          </Button>
        </div>
      </div>

      <div className="p-4 space-y-4">
        {/* Route Stops (read-only) — recorded depot & stop times for haulage/multi-stop jobs */}
        {(job?.job_type === 'haulage' || job?.requires_multi_stops) && (job?.stops?.length || job?.depot_departed_at || job?.depot_arrived_at) ? (
          (() => {
            const tfmt = (iso) => {
              if (!iso) return null;
              return formatTimeFromDate(iso, tenantSettings?.time_format) || (iso.split('T')[1] || '').slice(0, 5) || null;
            };
            const sortedStops = (job.stops || []).slice().sort((a, b) => (a.order || 0) - (b.order || 0));
            return (
              <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]" data-testid="docket-route-stops">
                <CardHeader className="pb-2">
                  <CardTitle className="text-[var(--m-text)] text-sm flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-[#ffd108]" /> Route Stops
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {(job.depot_departed_at || job.depot_arrived_at) && (
                    <div className="text-xs bg-[var(--m-bg-input)] rounded-lg px-3 py-2 space-y-1" data-testid="docket-depot-times">
                      <div className="flex items-center justify-between">
                        <span className="text-[var(--m-text-secondary)]">Depot Depart <span className="text-[var(--m-text)] font-semibold ml-1">{tfmt(job.depot_departed_at) || '—'}</span></span>
                        <span className="text-[var(--m-text-secondary)]">Depot Arrival <span className="text-[var(--m-text)] font-semibold ml-1">{tfmt(job.depot_arrived_at) || '—'}</span></span>
                      </div>
                      {(job.depot_departed_km != null || job.depot_arrived_km != null) && (
                        <div className="flex items-center justify-between text-[var(--m-text-muted)]">
                          <span>{job.depot_departed_km != null ? `${job.depot_departed_km} km` : ''}</span>
                          <span>{job.depot_arrived_km != null ? `${job.depot_arrived_km} km` : ''}</span>
                        </div>
                      )}
                      {job.depot_departed_km != null && job.depot_arrived_km != null && job.depot_arrived_km >= job.depot_departed_km && (
                        <div className="flex items-center justify-between border-t border-[var(--m-border)] pt-1">
                          <span className="text-[var(--m-text-secondary)]">Distance</span>
                          <span className="text-[#ffd108] font-semibold">{job.depot_arrived_km - job.depot_departed_km} km</span>
                        </div>
                      )}
                    </div>
                  )}
                  {sortedStops.map((s, i) => (
                    <div key={s.id || i} className="flex items-center gap-3 text-xs border border-[var(--m-border)] rounded-lg px-3 py-2" data-testid={`docket-stop-${i}`}>
                      <span className="w-5 h-5 rounded-full bg-[var(--m-bg)] flex items-center justify-center text-[#ffd108] font-semibold shrink-0">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-[var(--m-text)] truncate">{s.label || [s.address, s.suburb].filter(Boolean).join(', ') || `Stop ${i + 1}`}</div>
                        <div className="text-[var(--m-text-muted)]">{(s.type || 'stop')}{s.est_arrival ? ` · ETA ${formatTimeUtil(s.est_arrival, tenantSettings?.time_format)}` : ''}</div>
                        {s.work_description && <div className="text-[var(--m-text-body)] mt-0.5"><span className="text-[#ffd108]">Works:</span> {s.work_description}</div>}
                      </div>
                      <div className="text-right shrink-0">
                        <div className="text-[#ffd108] font-semibold">{tfmt(s.arrived_at) || '—'}</div>
                        <div className="text-[var(--m-text-muted)] capitalize">{s.status === 'completed' ? 'done' : (s.status || '')}</div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })()
        ) : null}
        {/* Hours Entry — hidden for haulage/multi-stop (times come from Depot Depart/Arrive) */}
        {!isDepotJob && (
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-[var(--m-text)] text-sm">Hours Worked</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {items.map((item) => (
              <div key={item.id} className="border border-[var(--m-border)] rounded-lg p-3">
                <div className="flex items-center gap-2 mb-1">
                  {item.item_type === 'equipment' ? (
                    <Truck className="h-4 w-4 text-[#ffd108]" />
                  ) : (
                    <User className="h-4 w-4 text-blue-400" />
                  )}
                  <span className="text-[var(--m-text)] font-medium">{item.type_name}</span>
                  <Badge variant="secondary" className="ml-auto text-xs">
                    {item.item_type}
                  </Badge>
                </div>
                
                {/* Show Worker Name for workers */}
                {item.item_type === 'worker' && item.worker_name && (
                  <div className="text-xs text-blue-400 mb-3 ml-6">
                    {item.worker_name}
                  </div>
                )}
                
                {/* Full time picker for equipment/workers */}
                <>
                <div className="space-y-3">
                  {/* Depot Mode: Depart Depot */}
                  {depotMode && item.item_type !== 'attachment' && (
                    <div>
                      <Label className="text-[var(--m-text-secondary)] text-xs">Depart Depot</Label>
                      <div className="mt-1">
                        <WheelTimePicker
                          value={item.depart_depot_time || ''}
                          onChange={(v) => handleItemChange(item.id, 'depart_depot_time', v)}
                          minuteStep={5}
                          className="w-full h-10 bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] hover:bg-[var(--m-bg)] hover:text-[var(--m-text)]"
                          testId={`docket-depart-depot-${item.id}`}
                        />
                      </div>
                    </div>
                  )}

                  {/* Start Time / Arrive Site */}
                  <div>
                    <Label className="text-[var(--m-text-secondary)] text-xs">{depotMode && item.item_type !== 'attachment' ? 'Arrive Site' : 'Start Time'}</Label>
                    <div className="mt-1">
                      <WheelTimePicker
                        value={item.start_time || '08:00'}
                        onChange={(v) => handleItemChange(item.id, 'start_time', v)}
                        minuteStep={5}
                        className="w-full h-10 bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] hover:bg-[var(--m-bg)] hover:text-[var(--m-text)]"
                        testId={`docket-start-time-${item.id}`}
                      />
                    </div>
                  </div>

                  {/* Finish Time / Depart Site */}
                  <div>
                    <Label className="text-[var(--m-text-secondary)] text-xs">{depotMode && item.item_type !== 'attachment' ? 'Depart Site' : 'Finish Time'}</Label>
                    <div className="mt-1">
                      <WheelTimePicker
                        value={item.finish_time || '17:00'}
                        onChange={(v) => handleItemChange(item.id, 'finish_time', v)}
                        minuteStep={5}
                        className="w-full h-10 bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] hover:bg-[var(--m-bg)] hover:text-[var(--m-text)]"
                        testId={`docket-finish-time-${item.id}`}
                      />
                    </div>
                  </div>

                  {/* Depot Mode: Arrive Depot */}
                  {depotMode && item.item_type !== 'attachment' && (
                    <div>
                      <Label className="text-[var(--m-text-secondary)] text-xs">Arrive Depot</Label>
                      <div className="mt-1">
                        <WheelTimePicker
                          value={item.arrive_depot_time || ''}
                          onChange={(v) => handleItemChange(item.id, 'arrive_depot_time', v)}
                          minuteStep={5}
                          className="w-full h-10 bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] hover:bg-[var(--m-bg)] hover:text-[var(--m-text)]"
                          testId={`docket-arrive-depot-${item.id}`}
                        />
                      </div>
                    </div>
                  )}
                  
                  {/* Lunch and Travel */}
                  <div className={`${depotMode && item.item_type !== 'attachment' ? '' : 'grid grid-cols-2 gap-3'} pt-2 border-t border-[var(--m-border)]`}>
                    {/* Lunch Break */}
                    <div>
                      <Label className="text-[var(--m-text-secondary)] text-xs flex items-center gap-1">
                        <span className="text-red-400">−</span> Lunch Break
                      </Label>
                      <Select
                        value={String(item.lunch_minutes || 0)}
                        onValueChange={(v) => handleItemChange(item.id, 'lunch_minutes', parseInt(v))}
                      >
                        <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[200px] bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]">
                          {lunchTravelOptions.map(mins => (
                            <SelectItem key={mins} value={String(mins)}>
                              {mins === 0 ? 'None' : `${mins} min`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Travel Time — manual select when NOT in depot mode */}
                    {!(depotMode && item.item_type !== 'attachment') && (
                    <div>
                      <Label className="text-[var(--m-text-secondary)] text-xs flex items-center gap-1">
                        <span className="text-green-400">+</span> Travel Time
                      </Label>
                      <Select
                        value={String(item.travel_minutes || 0)}
                        onValueChange={(v) => handleItemChange(item.id, 'travel_minutes', parseInt(v))}
                      >
                        <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                        <SelectContent className="max-h-[200px] bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]">
                          {travelOptions.map(mins => (
                            <SelectItem key={mins} value={String(mins)}>
                              {mins === 0 ? 'None' : `${mins} min`}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    )}

                    {/* Travel read-only line in depot mode */}
                    {depotMode && item.item_type !== 'attachment' && (
                      <div className="mt-2">
                        <Label className="text-[var(--m-text-secondary)] text-xs flex items-center gap-1">
                          <span className="text-green-400">+</span> Travel
                        </Label>
                        <p className="text-sm text-[var(--m-text)] mt-1 font-mono" data-testid={`depot-travel-display-${item.id}`}>
                          {item.travel_out_minutes != null && item.travel_return_minutes != null
                            ? `${item.travel_out_minutes}min out + ${item.travel_return_minutes}min return`
                            : '— (enter depot times)'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Depot time validation errors */}
                {depotMode && item.item_type !== 'attachment' && item.uom === 'HOUR' && (
                  <>
                    {submitAttempted && (!item.depart_depot_time || !item.arrive_depot_time) && (item.start_time && item.finish_time) && (
                      <p className="text-xs text-red-400 mt-2" data-testid={`depot-error-missing-${item.id}`}>
                        Enter Depart Depot and Arrive Depot times
                      </p>
                    )}
                    {(item.travel_out_minutes || 0) > 720 && (
                      <p className="text-xs text-red-400 mt-1" data-testid={`depot-error-out-${item.id}`}>
                        Depart Depot must be before Arrive Site
                      </p>
                    )}
                    {(item.travel_return_minutes || 0) > 720 && (
                      <p className="text-xs text-red-400 mt-1" data-testid={`depot-error-return-${item.id}`}>
                        Arrive Depot must be after Depart Site
                      </p>
                    )}
                  </>
                )}

                {/* Calculated hours summary */}
                {item.start_time && item.finish_time && (
                  <div className="bg-[#ffd108]/10 border border-[#ffd108]/30 rounded p-2 mt-3">
                    <p className="text-[#ffd108] text-sm font-medium">
                      Calculated: {item.qty || 0} hours
                    </p>
                    <p className="text-xs text-[var(--m-text-secondary)] mt-1">
                      {depotMode && item.item_type !== 'attachment' && item.depart_depot_time && item.arrive_depot_time
                        ? <>({formatTimeUtil(item.depart_depot_time, tenantSettings.time_format)} → {formatTimeUtil(item.arrive_depot_time, tenantSettings.time_format)}){item.lunch_minutes > 0 && ` − ${item.lunch_minutes}min lunch`}</>
                        : <>({formatTimeUtil(item.start_time, tenantSettings.time_format)} → {formatTimeUtil(item.finish_time, tenantSettings.time_format)}){item.lunch_minutes > 0 && ` − ${item.lunch_minutes}min lunch`}{item.travel_minutes > 0 && ` + ${item.travel_minutes}min travel`}</>
                      }
                    </p>
                  </div>
                )}

                {/* SMU readings (equipment items only, when tenant has SMU tracking on) */}
                {item.item_type === 'equipment' && tenantSettings?.field_rules?.smu?.enabled && (
                  <div className="border border-[var(--m-border)] rounded p-2 mt-3 bg-[var(--m-bg-input)]" data-testid={`smu-block-${item.id}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-[10px] uppercase tracking-wide text-[var(--m-text-secondary)]">Meter Readings</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2 mb-2">
                      <div>
                        <Label className="text-[10px] text-[var(--m-text-secondary)]">Start ({item.smu_unit || 'Hours'})</Label>
                        <Input
                          type="number"
                          step="0.1"
                          inputMode="decimal"
                          className="h-9 bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]"
                          value={item.start_smu ?? ''}
                          onChange={(e) => handleItemChange(item.id, 'start_smu', e.target.value)}
                          placeholder="e.g. 1245.3"
                          data-testid={`smu-start-${item.id}`}
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] text-[var(--m-text-secondary)]">Finish ({item.smu_unit || 'Hours'})</Label>
                        <Input
                          type="number"
                          step="0.1"
                          inputMode="decimal"
                          className="h-9 bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]"
                          value={item.finish_smu ?? ''}
                          onChange={(e) => handleItemChange(item.id, 'finish_smu', e.target.value)}
                          placeholder="e.g. 1253.7"
                          data-testid={`smu-finish-${item.id}`}
                        />
                      </div>
                    </div>
                    {tenantSettings.field_rules.smu.secondary_meter_enabled && (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <Label className="text-[10px] text-[var(--m-text-secondary)]">Start ({item.smu_unit_2 || 'KM'})</Label>
                          <Input
                            type="number"
                            step="0.1"
                            inputMode="decimal"
                            className="h-9 bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]"
                            value={item.start_smu_2 ?? ''}
                            onChange={(e) => handleItemChange(item.id, 'start_smu_2', e.target.value)}
                            data-testid={`smu2-start-${item.id}`}
                          />
                        </div>
                        <div>
                          <Label className="text-[10px] text-[var(--m-text-secondary)]">Finish ({item.smu_unit_2 || 'KM'})</Label>
                          <Input
                            type="number"
                            step="0.1"
                            inputMode="decimal"
                            className="h-9 bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]"
                            value={item.finish_smu_2 ?? ''}
                            onChange={(e) => handleItemChange(item.id, 'finish_smu_2', e.target.value)}
                            data-testid={`smu2-finish-${item.id}`}
                          />
                        </div>
                      </div>
                    )}
                    {item.start_smu != null && item.finish_smu != null && item.start_smu !== '' && item.finish_smu !== '' && (
                      <div className="text-[11px] text-[var(--m-text-body)] mt-2">
                        Delta: <span className="text-[#ffd108] font-medium">{(parseFloat(item.finish_smu) - parseFloat(item.start_smu)).toFixed(1)}</span> {item.smu_unit || 'Hours'}
                        {parseFloat(item.finish_smu) < parseFloat(item.start_smu) && (
                          <span className="text-amber-400 ml-2">⚠ finish &lt; start</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
                </>
              </div>
            ))}

            {/* Copy times button — visible when there are worker items */}
            {hasWorkerItems && (
              <Button
                type="button"
                variant="outline"
                className="w-full border-blue-500/40 text-blue-400 hover:bg-blue-500/10 h-10"
                onClick={handleCopyTimesToWorkers}
                data-testid="copy-times-to-workers-btn"
              >
                <Copy className="h-4 w-4 mr-2" />
                Copy Asset Times to Workers
              </Button>
            )}
          </CardContent>
        </Card>
        )}

        {/* Attachments Section */}
        {availableAttachmentTypes.length > 0 && (
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[var(--m-text)] text-sm flex items-center gap-2">
                <Wrench className="h-4 w-4 text-[#ffd108]" />
                Attachments
              </CardTitle>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => { setAttachmentForm({ attachment_type_id: '', qty: '' }); setAttachmentDialogOpen(true); }}
                className="h-8 text-xs border-[#ffd108] text-[#ffd108] hover:bg-[#ffd108]/10"
                data-testid="add-attachment-btn"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Attachment
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {attachmentItems.length > 0 ? (
              <div className="space-y-2">
                {attachmentItems.map((att, idx) => (
                  <div key={att.id} className="flex items-center justify-between border border-[var(--m-border)] rounded-lg p-3">
                    <div className="flex items-center gap-2">
                      <Wrench className="h-4 w-4 text-[#ffd108]" />
                      <div>
                        <span className="text-[var(--m-text)] text-sm font-medium">{att.type_name}</span>
                        <p className="text-[#ffd108] text-xs font-mono">{att.qty} hr{att.qty !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setAttachmentItems(prev => prev.filter((_, i) => i !== idx))}
                      className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                      data-testid={`remove-attachment-${idx}`}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[var(--m-text-muted)] text-sm text-center py-3">No attachments added</p>
            )}
          </CardContent>
        </Card>
        )}

        {/* Material Loads — hidden for crane_hire and concrete_pumping tenants */}
        {!isCraneHire && !isConcretePumping && (
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[var(--m-text)] text-sm flex items-center gap-2">
                <Package className="h-4 w-4 text-[#ffd108]" />
                Material Loads
              </CardTitle>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => openLoadDialog()}
                className="h-8 text-xs border-[#ffd108] text-[#ffd108] hover:bg-[#ffd108]/10"
                data-testid="add-load-btn"
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Load
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {materialLoads.length === 0 ? (
              <div className="text-center py-6 text-[var(--m-text-muted)]">
                <Mountain className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No material loads added</p>
                <p className="text-xs mt-1">Tap "Add Load" to record pickups or disposals</p>
              </div>
            ) : (
              <div className="space-y-3">
                {materialLoads.map((load, index) => (
                  <div 
                    key={`item-${index}`}
                    className={`border rounded-lg p-3 ${
                      load.on_customer_account
                        ? 'bg-amber-500/10 border-amber-500/30'
                        : 'bg-[var(--m-bg)] border-[var(--m-border)]'
                    }`}
                    data-testid={`material-load-${index}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <span className="text-[var(--m-text)] font-medium">{load.material_name}</span>
                          <Badge className={getLoadTypeColor(load.load_type)}>
                            {getLoadTypeLabel(load.load_type)}
                          </Badge>
                          {load.on_customer_account && (
                            <span className="text-[10px] font-semibold uppercase px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-400" data-testid={`load-customer-badge-${index}`}>
                              Customer Acct
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-[var(--m-text-secondary)]">{load.yard_name}</p>
                        <div className="flex items-center gap-3 mt-2">
                          <span className="text-[#ffd108] font-semibold">
                            {load.quantity} {load.unit}
                          </span>
                          {load.tip_docket_number && (
                            <span className="text-xs text-[var(--m-text-muted)]">
                              Docket: {load.tip_docket_number}
                            </span>
                          )}
                          {load.tip_docket_photo && (
                            <Camera className="h-3 w-3 text-green-400" />
                          )}
                        </div>
                        {load.notes && (
                          <p className="text-xs text-[var(--m-text-muted)] mt-1">{load.notes}</p>
                        )}
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 text-[var(--m-text-secondary)] hover:text-[var(--m-text)]"
                          onClick={() => openLoadDialog(index)}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon" 
                          className="h-8 w-8 text-red-400 hover:text-red-300"
                          onClick={() => handleDeleteLoad(index)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Concrete Loads — concrete pumping tenants only */}
        {isConcretePumping && (
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[var(--m-text)] text-sm flex items-center gap-2">
                <Package className="h-4 w-4 text-[#ffd108]" />
                Concrete Pumped <span className="text-red-500">*</span>
              </CardTitle>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 border-[var(--m-border-light)] text-[var(--m-text-body)]"
                onClick={() => { setConcreteForm({ material_type_id: '', quantity: '' }); setConcreteDialogOpen(true); }}
                data-testid="add-concrete-btn"
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Add Concrete
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {concreteLoads.length === 0 ? (
              <p className="text-sm text-[var(--m-text-muted)]" data-testid="concrete-empty-state">
                No concrete loads added yet. Tap "Add Concrete" to begin.
              </p>
            ) : (
              <div className="space-y-2" data-testid="concrete-loads-list">
                {concreteLoads.map((cl, idx) => (
                  <div key={idx} className="flex items-center justify-between bg-[var(--m-bg)] border border-[var(--m-border)] rounded-lg px-3 py-2" data-testid={`concrete-load-${idx}`}>
                    <div>
                      <p className="text-sm text-[var(--m-text)] font-medium">{cl.material_name}</p>
                      <p className="text-xs text-[var(--m-text-secondary)]">{cl.quantity} m³</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setConcreteLoads(prev => prev.filter((_, i) => i !== idx))}
                      className="p-1.5 text-[var(--m-text-muted)] hover:text-red-400"
                      data-testid={`concrete-load-delete-${idx}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        )}

        {/* Add Concrete dialog */}
        <Dialog open={concreteDialogOpen} onOpenChange={setConcreteDialogOpen}>
          <DialogContent className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] max-w-sm mx-auto">
            <DialogHeader>
              <DialogTitle className="text-[var(--m-text)]">Add Concrete Load</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label className="text-[var(--m-text-body)]">Concrete Type</Label>
                <Select
                  value={concreteForm.material_type_id}
                  onValueChange={(v) => setConcreteForm(prev => ({ ...prev, material_type_id: v }))}
                >
                  <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]" data-testid="concrete-type-select">
                    <SelectValue placeholder="Select concrete type" />
                  </SelectTrigger>
                  <SelectContent>
                    {materialTypes.filter(mt => mt.is_concrete_type).map(mt => (
                      <SelectItem key={mt.id} value={mt.id}>{mt.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-[var(--m-text-body)]">Amount (m³)</Label>
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.1"
                  min="0"
                  value={concreteForm.quantity}
                  onChange={(e) => setConcreteForm(prev => ({ ...prev, quantity: e.target.value }))}
                  placeholder="0.0"
                  className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]"
                  data-testid="concrete-quantity-input"
                />
              </div>
            </div>
            <DialogFooter className="flex gap-2 mt-4">
              <Button type="button" variant="outline" onClick={() => setConcreteDialogOpen(false)} className="border-[var(--m-border-light)] text-[var(--m-text-body)]">Cancel</Button>
              <Button
                type="button"
                className="btn-volt"
                data-testid="concrete-add-confirm-btn"
                disabled={!concreteForm.material_type_id || !concreteForm.quantity || parseFloat(concreteForm.quantity) <= 0}
                onClick={() => {
                  const mt = materialTypes.find(m => m.id === concreteForm.material_type_id);
                  if (!mt) return;
                  setConcreteLoads(prev => [...prev, {
                    material_type_id: mt.id,
                    material_name: mt.name,
                    quantity: parseFloat(concreteForm.quantity),
                  }]);
                  setConcreteDialogOpen(false);
                }}
              >
                Add
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Tolls */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[var(--m-text)] text-sm flex items-center gap-2">
                <Receipt className="h-4 w-4 text-[#ffd108]" />
                Tolls
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setTollDialogOpen(true)}
                className="h-8 text-xs border-[#ffd108] text-[#ffd108] hover:bg-[#ffd108]/10"
                data-testid="add-toll-btn"
                disabled={availableTolls.length === 0}
              >
                <Plus className="h-3 w-3 mr-1" />
                Add Toll
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {docketTolls.length === 0 ? (
              <div className="text-center py-6 text-[var(--m-text-muted)]">
                <Receipt className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p className="text-sm">No tolls added</p>
                <p className="text-xs mt-1">
                  {availableTolls.length === 0 ? 'No tolls configured by your admin' : 'Tap "Add Toll" to add a toll'}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                {docketTolls.map((toll) => (
                  <div
                    key={toll.toll_id}
                    className="bg-[var(--m-bg)] border border-[var(--m-border)] rounded-lg p-3 flex items-center justify-between"
                    data-testid={`docket-toll-${toll.toll_id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <p className="text-[var(--m-text)] font-medium truncate">{toll.name}</p>
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 border-[var(--m-border-light)] text-[var(--m-text)]"
                        onClick={() => updateTollQty(toll.toll_id, -1)}
                        data-testid={`toll-${toll.toll_id}-decrease`}
                      >
                        <Minus className="h-4 w-4" />
                      </Button>
                      <span className="text-[var(--m-text)] font-mono w-6 text-center" data-testid={`toll-${toll.toll_id}-qty`}>
                        {toll.quantity}
                      </span>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-8 w-8 border-[var(--m-border-light)] text-[var(--m-text)]"
                        onClick={() => updateTollQty(toll.toll_id, 1)}
                        data-testid={`toll-${toll.toll_id}-increase`}
                      >
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Work Completed - Description for each item */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-[var(--m-text)] text-sm">Work Completed</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={workCompleted}
              onChange={(e) => setWorkCompleted(e.target.value)}
              placeholder="Describe all work completed during this job..."
              className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]"
              rows={4}
            />
          </CardContent>
        </Card>

        {/* Additional Notes */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-[var(--m-text)] text-sm">Additional Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Additional notes..."
              className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]"
              rows={3}
            />
          </CardContent>
        </Card>

        {/* Docket Photos */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[var(--m-text)] text-sm flex items-center gap-2">
                <Camera className="h-4 w-4 text-[#ffd108]" />
                Photos
              </CardTitle>
              <span className="text-[var(--m-text-muted)] text-xs">{docketPhotos.length}/{MAX_PHOTOS}</span>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {/* Photo grid */}
            {docketPhotos.length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {docketPhotos.map((photo) => (
                  <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden border border-[var(--m-border)] group" data-testid={`docket-photo-${photo.id}`}>
                    <img src={photo.url} alt="" className="w-full h-full object-cover" />
                    {photo.has_timestamp_overlay && (
                      <div className="absolute top-1 left-1">
                        <Badge className="bg-black/60 text-[var(--m-text)] text-[8px] border-0 px-1 py-0">Timestamped</Badge>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={() => removePhoto(photo.id)}
                      className="absolute top-1 right-1 bg-red-600/90 hover:bg-red-500 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                      data-testid={`remove-photo-${photo.id}`}
                    >
                      <X className="h-3 w-3 text-[var(--m-text)]" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Capture / Upload buttons */}
            {docketPhotos.length < MAX_PHOTOS && (
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 border-[var(--m-border-light)] text-[var(--m-text)] h-11"
                  onClick={() => cameraInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  data-testid="docket-photo-camera-btn"
                >
                  {uploadingPhoto ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Camera className="h-4 w-4 mr-2 text-[#ffd108]" />}
                  Take Photo
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1 border-[var(--m-border-light)] text-[var(--m-text)] h-11"
                  onClick={() => libraryInputRef.current?.click()}
                  disabled={uploadingPhoto}
                  data-testid="docket-photo-library-btn"
                >
                  {uploadingPhoto ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2 text-[#ffd108]" />}
                  From Library
                </Button>
              </div>
            )}

            {/* Hidden file inputs */}
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => handleDocketPhotoCapture(e, 'camera')}
            />
            <input
              ref={libraryInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => handleDocketPhotoCapture(e, 'library')}
            />

            {docketPhotos.length === 0 && (
              <p className="text-[var(--m-text-muted)] text-xs text-center">Add photos of completed work, site conditions, or equipment</p>
            )}
          </CardContent>
        </Card>

        {/* Customer Sign-off */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-[var(--m-text)] text-sm flex items-center gap-2">
              <PenTool className="h-4 w-4 text-[#ffd108]" />
              Customer Sign-off
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Start-of-day / anytime authorisation banner (when captured) */}
            {job?.start_signoff && ['start', 'both', 'anytime'].includes(job?.signature_config?.timing) && (
              <div className="p-3 rounded-lg bg-green-500/5 border border-green-600/30" data-testid="docket-start-signoff-banner">
                {job.start_signoff.skipped ? (
                  <p className="text-xs text-amber-400">{job.signature_config?.timing === 'anytime' ? 'Customer authorisation' : 'Start-of-day sign-on'} skipped — {job.start_signoff.skip_reason === 'no_one_on_site' ? 'No one on site' : job.start_signoff.skip_reason === 'customer refused' ? 'Customer refused' : 'Other'}</p>
                ) : (
                  <div className="flex items-center gap-3">
                    {job.start_signoff.signature && <img src={job.start_signoff.signature} alt="Authorisation signature" className="h-10 bg-white rounded border border-[var(--m-border-light)]" />}
                    <p className="text-xs text-[var(--m-text-body)]">{job.signature_config?.timing === 'anytime' ? 'Authorised' : 'Authorised at start of day'} by <span className="text-[var(--m-text)] font-medium">{job.start_signoff.customer_name}</span></p>
                  </div>
                )}
              </div>
            )}
            {job?.signature_config?.timing === 'start' && (
              <p className="text-xs text-[var(--m-text-muted)]" data-testid="docket-end-signature-optional-note">End-of-day signature is optional for this job (customer signs at the start of the day).</p>
            )}
            {job?.signature_config?.timing === 'anytime' && job?.start_signoff && !job.start_signoff.skipped && (
              <p className="text-xs text-[var(--m-text-muted)]" data-testid="docket-end-signature-optional-note">End-of-day signature is optional — customer has already authorised this job.</p>
            )}
            {job?.signature_config?.timing === 'anytime' && (!job?.start_signoff || job.start_signoff.skipped) && (
              <p className="text-xs text-amber-400/80" data-testid="docket-end-signature-required-note">No customer authorisation captured — end-of-day signature is required.</p>
            )}

            {/* Skip signature toggle */}
            <div 
              className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${signatureSkipped ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-[var(--m-bg)] border border-[var(--m-border)]'}`}
              onClick={() => {
                setSignatureSkipped(!signatureSkipped);
                if (!signatureSkipped) {
                  setSkipReason('');
                  setSkipNotes('');
                }
              }}
              data-testid="signature-skip-toggle"
            >
              <Checkbox
                checked={signatureSkipped}
                onCheckedChange={(checked) => {
                  setSignatureSkipped(checked);
                  if (checked) {
                    setSkipReason('');
                    setSkipNotes('');
                  }
                }}
                className="data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
              />
              <span className={`text-sm ${signatureSkipped ? 'text-amber-400 font-medium' : 'text-[var(--m-text-secondary)]'}`}>
                Unable to capture signature
              </span>
            </div>

            {signatureSkipped ? (
              <>
                {/* Skip reason selection */}
                <div>
                  <Label className="text-[var(--m-text-secondary)] text-xs mb-2 block">Reason <span className="text-red-500">*</span></Label>
                  <div className="space-y-2">
                    {[
                      { value: 'no_one_on_site', label: 'No one on site' },
                      { value: 'customer_refused', label: 'Customer refused' },
                      { value: 'other', label: 'Other' },
                    ].map((option) => (
                      <div
                        key={option.value}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          skipReason === option.value
                            ? 'bg-[#ffd108]/10 border border-[#ffd108]/40'
                            : 'bg-[var(--m-bg)] border border-[var(--m-border)]'
                        }`}
                        onClick={() => setSkipReason(option.value)}
                        data-testid={`skip-reason-${option.value}`}
                      >
                        <div className={`h-4 w-4 rounded-full border-2 flex items-center justify-center ${
                          skipReason === option.value
                            ? 'border-[#ffd108]'
                            : 'border-[var(--m-border-light)]'
                        }`}>
                          {skipReason === option.value && (
                            <div className="h-2 w-2 rounded-full bg-[#ffd108]" />
                          )}
                        </div>
                        <span className={`text-sm ${skipReason === option.value ? 'text-[var(--m-text)]' : 'text-[var(--m-text-secondary)]'}`}>
                          {option.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Skip notes */}
                <div>
                  <Label className="text-[var(--m-text-secondary)] text-xs">Additional Notes</Label>
                  <Textarea
                    value={skipNotes}
                    onChange={(e) => setSkipNotes(e.target.value)}
                    placeholder="Any additional details..."
                    className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1"
                    rows={3}
                    data-testid="skip-notes-input"
                  />
                </div>
              </>
            ) : (
              <>
                {/* Normal customer signature flow */}
                <div>
                  <Label className="text-[var(--m-text-secondary)] text-xs">Customer Name</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <User className="h-4 w-4 text-[var(--m-text-muted)]" />
                    <Input
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      placeholder="Name of person signing"
                      className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]"
                      data-testid="customer-name-input"
                    />
                  </div>
                </div>

                {/* Customer Signature Pad */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-[var(--m-text-secondary)] text-xs">Signature</Label>
                    {hasSignature && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={clearSignature}
                        className="text-red-400 h-6 px-2"
                      >
                        <Trash2 className="h-3 w-3 mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                  <div className="border border-[var(--m-border-light)] rounded-lg overflow-hidden">
                    <canvas
                      ref={canvasRef}
                      width={300}
                      height={150}
                      className="w-full touch-none"
                      style={{ background: '#ffffff' }}
                      onMouseDown={startDrawing}
                      onMouseMove={draw}
                      onMouseUp={stopDrawing}
                      onMouseLeave={stopDrawing}
                      onTouchStart={startDrawing}
                      onTouchMove={draw}
                      onTouchEnd={stopDrawing}
                    />
                  </div>
                  <p className="text-[var(--m-text-muted)] text-xs mt-1 text-center">Draw signature above</p>
                </div>

                {/* Customer Confirmation */}
                <div className="flex items-start gap-3 p-3 bg-[var(--m-bg)] rounded-lg">
                  <Checkbox
                    id="customer-confirm"
                    checked={customerConfirmed}
                    onCheckedChange={setCustomerConfirmed}
                    className="mt-0.5 data-[state=checked]:bg-[#ffd108] data-[state=checked]:border-[#ffd108]"
                    data-testid="customer-confirm-checkbox"
                  />
                  <label htmlFor="customer-confirm" className="text-[var(--m-text-body)] text-sm cursor-pointer">
                    I confirm that the work described above has been completed satisfactorily.
                  </label>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Driver Sign-off */}
        <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]">
          <CardHeader className="pb-2">
            <CardTitle className="text-[var(--m-text)] text-sm flex items-center gap-2">
              <PenTool className="h-4 w-4 text-[#ffd108]" />
              Driver Sign-off
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-[var(--m-text-secondary)] text-xs">Driver Name</Label>
              <div className="flex items-center gap-2 mt-1">
                <User className="h-4 w-4 text-[var(--m-text-muted)]" />
                <Input
                  value={driverName}
                  onChange={(e) => setDriverName(e.target.value)}
                  placeholder="Driver name"
                  className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]"
                  data-testid="driver-name-input"
                />
              </div>
            </div>

            {/* Driver Signature Pad */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-[var(--m-text-secondary)] text-xs">Signature</Label>
                {hasDriverSignature && (
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={clearDriverSignature}
                    className="text-red-400 h-6 px-2"
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
              <div className="border border-[var(--m-border-light)] rounded-lg overflow-hidden">
                <canvas
                  ref={driverCanvasRef}
                  width={300}
                  height={150}
                  className="w-full touch-none"
                  style={{ background: '#ffffff' }}
                  onMouseDown={startDriverDrawing}
                  onMouseMove={drawDriver}
                  onMouseUp={stopDriverDrawing}
                  onMouseLeave={stopDriverDrawing}
                  onTouchStart={startDriverDrawing}
                  onTouchMove={drawDriver}
                  onTouchEnd={stopDriverDrawing}
                />
              </div>
              <p className="text-[var(--m-text-muted)] text-xs mt-1 text-center">Draw signature above</p>
            </div>

            {/* Driver Confirmation */}
            <div className="flex items-start gap-3 p-3 bg-[var(--m-bg)] rounded-lg">
              <Checkbox
                id="driver-confirm"
                checked={driverConfirmed}
                onCheckedChange={setDriverConfirmed}
                className="mt-0.5 data-[state=checked]:bg-[#ffd108] data-[state=checked]:border-[#ffd108]"
                data-testid="driver-confirm-checkbox"
              />
              <label htmlFor="driver-confirm" className="text-[var(--m-text-body)] text-sm cursor-pointer">
                I confirm that the work described above has been completed satisfactorily.
              </label>
            </div>
          </CardContent>
        </Card>

        {/* Project Tasks Completed — read-only list of tasks completed on this docket date */}
        {isFlagEnabled('projects') && job?.project_tasks && (() => {
          const todayTasks = (job.project_tasks || []).filter(
            t => t.status === 'completed' && t.completion?.job_date === jobDate
          );
          if (todayTasks.length === 0) return null;
          return (
            <Card className="bg-[var(--m-bg-card)] border-[var(--m-border)]" data-testid="docket-project-tasks-section">
              <CardHeader className="pb-2">
                <CardTitle className="text-[var(--m-text)] text-sm flex items-center gap-2">
                  <Layers className="h-4 w-4 text-[#ffd108]" />
                  Project Tasks Completed
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {todayTasks.map(task => (
                  <div key={task.id} className="flex items-start gap-2 p-2 rounded bg-green-500/5 border border-green-500/20" data-testid={`docket-task-${task.id}`}>
                    <CheckCircle2 className="h-4 w-4 text-green-500 shrink-0 mt-0.5" />
                    <div className="text-xs">
                      <p className="text-[var(--m-text)] font-medium">{task.description}</p>
                      {task.completion && (
                        <div className="text-[var(--m-text-muted)] mt-0.5 space-y-0.5">
                          {task.completion.operator_name && <span className="flex items-center gap-1"><User className="h-3 w-3" />{task.completion.operator_name}</span>}
                          {(task.completion.start_time || task.completion.finish_time) && (
                            <span className="flex items-center gap-1">
                              <Clock className="h-3 w-3" />
                              {task.completion.start_time ? new Date(task.completion.start_time).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : '?'} - {task.completion.finish_time ? new Date(task.completion.finish_time).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'}) : '?'}
                            </span>
                          )}
                          {task.completion.work_description && <span className="flex items-center gap-1"><FileText className="h-3 w-3" />{task.completion.work_description}</span>}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })()}

        {/* Submit Action — inline at end of form */}
        <Button
          className="w-full btn-volt h-12 text-base"
          onClick={handleSubmit}
          disabled={saving}
          data-testid="submit-docket-btn"
        >
          {saving ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Check className="h-4 w-4 mr-2" />
          )}
          Submit Docket
        </Button>
      </div>

      {/* Add Attachment Dialog */}
      <Dialog open={attachmentDialogOpen} onOpenChange={setAttachmentDialogOpen}>
        <DialogContent className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] w-[calc(100%-2rem)] max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-[var(--m-text)] flex items-center gap-2">
              <Wrench className="h-5 w-5 text-[#ffd108]" />
              Add Attachment
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label className="text-[var(--m-text-secondary)] text-xs">Attachment Type</Label>
              <Select
                value={attachmentForm.attachment_type_id}
                onValueChange={(v) => setAttachmentForm(prev => ({ ...prev, attachment_type_id: v }))}
              >
                <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)]" data-testid="attachment-type-select">
                  <SelectValue placeholder="Select attachment" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px] bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)]">
                  {(() => {
                    const addedIds = attachmentItems.map(a => a.attachment_type_id);
                    const jobConfigured = availableAttachmentTypes.filter(at => jobAttachmentTypeIds.includes(at.id) && !addedIds.includes(at.id));
                    const remaining = availableAttachmentTypes.filter(at => !jobAttachmentTypeIds.includes(at.id) && !addedIds.includes(at.id));
                    return (
                      <>
                        {jobConfigured.length > 0 && (
                          <>
                            <div className="px-2 py-1 text-xs text-[var(--m-text-secondary)] font-medium">Job Requirements</div>
                            {jobConfigured.map(at => (
                              <SelectItem key={at.id} value={at.id}>{at.name}</SelectItem>
                            ))}
                          </>
                        )}
                        {jobConfigured.length > 0 && remaining.length > 0 && (
                          <div className="border-t border-[var(--m-border-light)] my-1" />
                        )}
                        {remaining.length > 0 && (
                          <>
                            <div className="px-2 py-1 text-xs text-[var(--m-text-secondary)] font-medium">Available</div>
                            {remaining.map(at => (
                              <SelectItem key={at.id} value={at.id}>{at.name}</SelectItem>
                            ))}
                          </>
                        )}
                        {jobConfigured.length === 0 && remaining.length === 0 && (
                          <div className="px-2 py-3 text-sm text-[var(--m-text-muted)] text-center">All attachments already added</div>
                        )}
                      </>
                    );
                  })()}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label className="text-[var(--m-text-secondary)] text-xs">Units (hours)</Label>
              <Input
                type="number"
                step="0.5"
                min="0"
                value={attachmentForm.qty}
                onChange={(e) => setAttachmentForm(prev => ({ ...prev, qty: e.target.value }))}
                placeholder="0"
                className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] font-mono"
                data-testid="attachment-qty-input"
              />
            </div>
          </div>
          <DialogFooter className="flex-col gap-2 sm:flex-row sm:gap-0 pt-2">
            <Button
              variant="outline"
              onClick={() => setAttachmentDialogOpen(false)}
              className="border-[var(--m-border-light)] text-[var(--m-text-body)] hover:bg-[var(--m-bg-elevated)] w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                const attType = availableAttachmentTypes.find(at => at.id === attachmentForm.attachment_type_id);
                if (!attType) return;
                setAttachmentItems(prev => [...prev, {
                  id: 'attachment-' + attType.id + '-' + Date.now(),
                  item_type: 'attachment',
                  type_name: attType.name,
                  attachment_type_id: attType.id,
                  qty: parseFloat(attachmentForm.qty) || 0,
                  uom: 'HOUR'
                }]);
                setAttachmentDialogOpen(false);
                setAttachmentForm({ attachment_type_id: '', qty: '' });
              }}
              disabled={!attachmentForm.attachment_type_id}
              className="bg-[#ffd108] text-black hover:bg-[#ffd108]/90 w-full sm:w-auto"
              data-testid="attachment-save-btn"
            >
              Add Attachment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Material Load Dialog — hidden for crane_hire tenants */}
      {!isCraneHire && (
      <Dialog open={loadDialogOpen} onOpenChange={setLoadDialogOpen}>
        <DialogContent className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] w-[calc(100%-2rem)] max-w-md rounded-lg">
          <DialogHeader>
            <DialogTitle className="text-[var(--m-text)] flex items-center gap-2">
              <Package className="h-5 w-5 text-[#ffd108]" />
              {editingLoadIndex !== null ? 'Edit Load' : 'Add Load'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            {/* Location */}
            <div>
              <Label className="text-[var(--m-text-body)]">Location <span className="text-red-500">*</span></Label>
              <Select 
                value={loadForm.yard_id} 
                onValueChange={(v) => {
                  setLoadForm(prev => ({ 
                    ...prev, 
                    yard_id: v,
                    material_type_id: '', // Reset material when yard changes
                    unit: 'tonne'
                  }));
                }}
              >
                <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1">
                  <SelectValue placeholder="Select tip or quarry" />
                </SelectTrigger>
                <SelectContent>
                  {yards.map(yard => (
                    <SelectItem key={yard.id} value={yard.id}>
                      {yard.name} ({yard.type})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {yards.length === 0 && (
                <p className="text-xs text-[var(--m-text-muted)] mt-1">No locations configured</p>
              )}
            </div>

            {/* Material Type */}
            <div>
              <Label className="text-[var(--m-text-body)]">Material Type <span className="text-red-500">*</span></Label>
              <Select 
                value={loadForm.material_type_id} 
                onValueChange={(v) => {
                  // Auto-select unit if only one is available
                  const availableUnits = getAvailableUnits();
                  setLoadForm(prev => ({ 
                    ...prev, 
                    material_type_id: v,
                    unit: availableUnits.length === 1 ? availableUnits[0] : prev.unit
                  }));
                }}
                disabled={!loadForm.yard_id}
              >
                <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1">
                  <SelectValue placeholder={loadForm.yard_id ? "Select material" : "Select location first"} />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableMaterials().map(mat => (
                    <SelectItem key={mat.id} value={mat.id}>
                      {mat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Load Type */}
            <div>
              <Label className="text-[var(--m-text-body)]">Load Type <span className="text-red-500">*</span></Label>
              <Select 
                value={loadForm.load_type} 
                onValueChange={(v) => setLoadForm(prev => ({ ...prev, load_type: v }))}
              >
                <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pickup">Pickup</SelectItem>
                  <SelectItem value="disposal">Disposal</SelectItem>
                  <SelectItem value="buyback">Buy Back</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Quantity and Unit */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-[var(--m-text-body)]">Quantity <span className="text-red-500">*</span></Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={loadForm.quantity}
                  onChange={(e) => setLoadForm(prev => ({ ...prev, quantity: e.target.value }))}
                  placeholder="0.00"
                  className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1"
                />
              </div>
              <div>
                <Label className="text-[var(--m-text-body)]">Unit <span className="text-red-500">*</span></Label>
                <Select 
                  value={loadForm.unit} 
                  onValueChange={(v) => setLoadForm(prev => ({ ...prev, unit: v }))}
                >
                  <SelectTrigger className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {getAvailableUnits().map(unit => (
                      <SelectItem key={unit} value={unit}>
                        {unit === 'm3' ? 'Cubic Metre (m³)' : unit.charAt(0).toUpperCase() + unit.slice(1)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Tip Docket Number */}
            <div>
              <Label className="text-[var(--m-text-body)]">Tip Docket Number</Label>
              <Input
                value={loadForm.tip_docket_number}
                onChange={(e) => setLoadForm(prev => ({ ...prev, tip_docket_number: e.target.value }))}
                placeholder="Docket # from tip/quarry"
                className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1"
              />
            </div>

            {/* Photo Upload */}
            <div>
              <Label className="text-[var(--m-text-body)]">Docket Photo</Label>
              <input
                ref={photoInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoCapture}
                className="hidden"
              />
              <div className="mt-1">
                {loadForm.tip_docket_photo ? (
                  <div className="relative">
                    <img 
                      src={loadForm.tip_docket_photo} 
                      alt="Docket" 
                      className="w-full h-32 object-cover rounded border border-[var(--m-border-light)]"
                    />
                    <Button
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2 h-7"
                      onClick={() => setLoadForm(prev => ({ ...prev, tip_docket_photo: null }))}
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Remove
                    </Button>
                  </div>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full border-dashed border-[var(--m-border-light)] text-[var(--m-text-secondary)] hover:text-[var(--m-text)]"
                    onClick={() => photoInputRef.current?.click()}
                  >
                    <Camera className="h-4 w-4 mr-2" />
                    Take Photo
                  </Button>
                )}
              </div>
            </div>

            {/* On Customer Account */}
            <div>
              <button
                type="button"
                onClick={() => setLoadForm(prev => ({ ...prev, on_customer_account: !prev.on_customer_account }))}
                className={`w-full flex items-center justify-between p-3 rounded-lg border transition-colors ${
                  loadForm.on_customer_account
                    ? 'bg-amber-500/15 border-amber-500/40 text-amber-400'
                    : 'bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text-secondary)]'
                }`}
                data-testid="load-customer-account-toggle"
              >
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">On Customer Account</span>
                  <span className="text-[10px] text-[var(--m-text-muted)]">(Not invoiced)</span>
                </div>
                <div className={`relative inline-flex h-5 w-9 shrink-0 rounded-full border-2 border-transparent transition-colors ${
                  loadForm.on_customer_account ? 'bg-amber-500' : 'bg-[var(--m-border-light)]'
                }`}>
                  <span className={`pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    loadForm.on_customer_account ? 'translate-x-4' : 'translate-x-0'
                  }`} />
                </div>
              </button>
            </div>

            {/* Notes */}
            <div>
              <Label className="text-[var(--m-text-body)]">Notes</Label>
              <Textarea
                value={loadForm.notes}
                onChange={(e) => setLoadForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Additional notes..."
                className="bg-[var(--m-bg)] border-[var(--m-border-light)] text-[var(--m-text)] mt-1"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:gap-0 pt-2">
            <Button 
              variant="outline" 
              onClick={() => setLoadDialogOpen(false)}
              className="border-[var(--m-border-light)] text-[var(--m-text-body)] w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button 
              className="btn-volt w-full sm:w-auto"
              onClick={handleSaveLoad}
            >
              {editingLoadIndex !== null ? 'Save Changes' : 'Add Load'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      {/* Toll Picker Dialog */}
      <Dialog open={tollDialogOpen} onOpenChange={setTollDialogOpen}>
        <DialogContent className="bg-[var(--m-bg-card)] border-[var(--m-border)] text-[var(--m-text)] w-[calc(100%-2rem)] max-w-md rounded-lg" data-testid="toll-picker-dialog">
          <DialogHeader>
            <DialogTitle className="text-[var(--m-text)]">Add Toll</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-[60vh] overflow-y-auto">
            {availableTolls.length === 0 ? (
              <p className="text-[var(--m-text-secondary)] text-sm text-center py-6">No tolls configured by your admin.</p>
            ) : (
              availableTolls.map(toll => (
                <button
                  key={toll.id}
                  type="button"
                  onClick={() => handleAddToll(toll)}
                  className="w-full flex items-center bg-[var(--m-bg)] border border-[var(--m-border)] rounded-lg p-3 text-left active:scale-[0.98] transition-transform"
                  data-testid={`toll-option-${toll.id}`}
                >
                  <span className="text-[var(--m-text)] font-medium truncate">{toll.name}</span>
                </button>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobileCreateDocket;
